import { useState, useEffect, useRef } from 'react'
import { Loader2, Plus, Trash2, Gift, AlertTriangle, CreditCard, ChevronDown, ChevronUp, Search, Download, X, DollarSign, Plane, Database, Sparkles, Send, TrendingUp, BarChart3, Info, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { api } from '../../api/client'

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
const pct = (n: number) => `${n.toFixed(1)}%`

const CATEGORIES = ['dining', 'groceries', 'travel', 'gas', 'drugstore', 'entertainment', 'streaming', 'subscription', 'other'] as const

const CAT_ICONS: Record<string, string> = {
  dining: '🍽️', groceries: '🛒', travel: '✈️', gas: '⛽', drugstore: '💊',
  entertainment: '🎬', streaming: '📺', subscription: '📱', other: '🛍️',
}

interface RewardRate { category: string; cashback_pct: number; points_multiplier: number }

interface CardRewards {
  card_id: string; db_card_id?: string; card_name: string; institution: string
  last4: string; reward_type: 'cashback' | 'points' | 'both'; default_rate: number
  rates: RewardRate[]; balance_cashback: number; balance_points: number; enabled: boolean
  card_network?: string; annual_fee?: number; annual_credits_value?: number
  rewards_currency?: string; point_value_cents?: number; foreign_tx_fee_pct?: number
  has_transfer_partners?: boolean; welcome_bonus_value?: number
}

interface MissedReward {
  merchant: string; amount: number; category: string; card_used: string
  better_card: string; potential_reward: number; actual_reward: number; difference: number
}
interface EarnedReward { merchant: string; amount: number; category: string; card_used: string; reward_earned: number }

interface CategoryBreakdown {
  category: string; spend: number; earned: number; optimal: number
  missed: number; txn_count: number; best_card: string; best_rate: number
}

interface PerCardStat {
  card_id: string; card_name: string; spend: number; earned: number
  optimal_earned: number; missed: number; txn_count: number
}

interface RewardsData {
  cards: CardRewards[]; missed_rewards: MissedReward[]; earned_rewards: EarnedReward[]
  total_earned: number; total_missed: number
  category_breakdown: CategoryBreakdown[]; per_card_stats: PerCardStat[]
}

interface RecommendResult {
  recommended: { card_id: string; card_name: string; institution: string; effective_rate: number; net_rate: number; reward_value: number; rewards_currency: string; has_portal_bonus: boolean; portal_bonus_reason: string | null; foreign_tx_fee_pct: number }
  alternatives: Array<{ card_id: string; card_name: string; effective_rate: number; net_rate: number; reward_value: number; has_portal_bonus: boolean; portal_bonus_reason: string | null }>
  detected_category: string; reason: string; tips: string[]; query: string; amount: number
}

interface DbCardSummary {
  card_id: string; card_name: string; issuer: string; card_network: string
  card_category: string; card_subcategory: string; annual_fee_usd: number
  rewards_currency: string; base_earn_rate: number; base_earn_rate_unit: string
  point_value_cashback_cents: number; point_value_transfer_cents: number
  foreign_transaction_fee_pct: number; has_transfer_partners: boolean
  annual_credits_value: number; welcome_bonus_value: number
  top_categories: Array<{ category: string; earn_rate: number; unit: string }>
}

export function RewardsPage() {
  const [data, setData] = useState<RewardsData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [editingCard, setEditingCard] = useState<string | null>(null)
  const [expandedMissed, setExpandedMissed] = useState(true)
  const [expandedEarned, setExpandedEarned] = useState(true)
  const [saving, setSaving] = useState(false)
  const [missedSort, setMissedSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'difference', dir: 'desc' })
  const [earnedSort, setEarnedSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'reward_earned', dir: 'desc' })
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'intelligence' | 'cards' | 'advisor'>('intelligence')

  // SmartPay Advisor state
  const [advisorQuery, setAdvisorQuery] = useState('')
  const [advisorAmount, setAdvisorAmount] = useState('')
  const [advisorIntl, setAdvisorIntl] = useState(false)
  const [advisorResult, setAdvisorResult] = useState<RecommendResult | null>(null)
  const [advisorLoading, setAdvisorLoading] = useState(false)

  // Database card picker state
  const [showDbPicker, setShowDbPicker] = useState(false)
  const [dbCards, setDbCards] = useState<DbCardSummary[]>([])
  const [dbSearch, setDbSearch] = useState('')
  const [dbLoading, setDbLoading] = useState(false)
  const [importing, setImporting] = useState<string | null>(null)
  const [dbFilter, setDbFilter] = useState<string>('all')
  const searchRef = useRef<HTMLInputElement>(null)

  const toggleMissedSort = (col: string) => {
    setMissedSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'merchant' || col === 'card_used' || col === 'better_card' ? 'asc' : 'desc' })
  }
  const toggleEarnedSort = (col: string) => {
    setEarnedSort(prev => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: col === 'merchant' || col === 'card_used' || col === 'category' ? 'asc' : 'desc' })
  }
  const SortIcon = ({ col, sort }: { col: string; sort: { col: string; dir: string } }) => {
    if (sort.col !== col) return <ArrowUpDown className="h-3 w-3 text-slate-600" />
    return sort.dir === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-400" /> : <ArrowDown className="h-3 w-3 text-indigo-400" />
  }

  const loadData = async () => {
    try {
      const d = await api.get<RewardsData>('/api/rewards')
      setData(d)
    } catch (e: any) { setError(e.message || 'Failed to load rewards data') }
    setLoading(false)
  }

  useEffect(() => { loadData() }, [])

  const saveCardRates = async (card: CardRewards) => {
    setSaving(true)
    try {
      await api.post('/api/rewards/cards', card)
      setEditingCard(null)
      await loadData()
    } catch (e: any) { setError(e.message) }
    setSaving(false)
  }

  const deleteCard = async (cardId: string) => {
    try {
      await api.delete(`/api/rewards/cards/${cardId}`)
      await loadData()
    } catch (e: any) { setError(e.message) }
  }

  const addManualCard = () => {
    const newCard: CardRewards = {
      card_id: `manual_${Date.now()}`, card_name: '', institution: '', last4: '',
      reward_type: 'cashback', default_rate: 1,
      rates: CATEGORIES.map(c => ({ category: c, cashback_pct: 1, points_multiplier: 1 })),
      balance_cashback: 0, balance_points: 0, enabled: true,
    }
    setData(prev => prev ? { ...prev, cards: [...prev.cards, newCard] } : prev)
    setEditingCard(newCard.card_id)
    setActiveTab('cards')
  }

  // ── SmartPay Advisor ──
  const askAdvisor = async (query?: string) => {
    const q = query ?? advisorQuery
    if (!q.trim()) return
    if (query) setAdvisorQuery(query)
    setAdvisorLoading(true)
    setAdvisorResult(null)
    try {
      const result = await api.post<RecommendResult>('/api/rewards/recommend', {
        description: q, amount: parseFloat(advisorAmount) || 100, is_international: advisorIntl,
      })
      setAdvisorResult(result)
    } catch (e: any) { setError(e.message) }
    setAdvisorLoading(false)
  }

  // ── Database picker helpers ──
  const openDbPicker = async () => {
    setShowDbPicker(true); setDbSearch(''); setDbFilter('all'); setDbLoading(true)
    try {
      const cards = await api.get<DbCardSummary[]>('/api/rewards/database/cards')
      setDbCards(cards)
    } catch (e: any) { setError(e.message) }
    setDbLoading(false)
    setTimeout(() => searchRef.current?.focus(), 100)
  }

  const importCard = async (dbCardId: string) => {
    setImporting(dbCardId)
    try {
      await api.post(`/api/rewards/import/${dbCardId}`)
      await loadData()
      setShowDbPicker(false)
    } catch (e: any) { setError(e.message) }
    setImporting(null)
  }

  const filteredDbCards = dbCards.filter(c => {
    const matchSearch = !dbSearch || `${c.card_name} ${c.issuer} ${c.card_id}`.toLowerCase().includes(dbSearch.toLowerCase())
    const matchFilter = dbFilter === 'all' || c.card_category.toLowerCase() === dbFilter
    return matchSearch && matchFilter
  })
  const existingDbCardIds = new Set((data?.cards ?? []).map(c => c.db_card_id).filter(Boolean))

  const issuerColor = (issuer: string) => {
    const colors: Record<string, string> = {
      'Chase': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'American Express': 'bg-sky-500/20 text-sky-300 border-sky-500/30',
      'Capital One': 'bg-red-500/20 text-red-300 border-red-500/30',
      'Citi': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      'Discover': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      'Wells Fargo': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      'Bank of America': 'bg-rose-500/20 text-rose-300 border-rose-500/30',
      'Barclays': 'bg-teal-500/20 text-teal-300 border-teal-500/30',
    }
    return colors[issuer] || 'bg-slate-500/20 text-slate-300 border-slate-500/30'
  }

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>

  const cards = data?.cards ?? []
  const missed = data?.missed_rewards ?? []
  const earned = data?.earned_rewards ?? []
  const catBreakdown = data?.category_breakdown ?? []
  const cardStats = data?.per_card_stats ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Rewards Intelligence</h2>
          <p className="text-sm text-slate-500">Analyze spend, optimize rewards, and get smart card recommendations</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={openDbPicker}
            className="flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-500 transition-colors">
            <Database className="h-4 w-4" /> Import Card
          </button>
          <button onClick={addManualCard}
            className="flex items-center gap-2 rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-700 transition-colors">
            <Plus className="h-4 w-4" /> Manual
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 rounded-xl bg-slate-800/60 p-1">
        {([
          { id: 'intelligence' as const, label: 'Spend Intelligence', icon: BarChart3 },
          { id: 'cards' as const, label: 'Your Cards', icon: CreditCard },
          { id: 'advisor' as const, label: 'SmartPay Advisor', icon: Sparkles },
        ]).map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              activeTab === tab.id ? 'bg-slate-700 text-white shadow-sm' : 'text-slate-400 hover:text-slate-200'
            }`}>
            <tab.icon className="h-4 w-4" /> {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-400 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="text-rose-400 hover:text-rose-300"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* ══ TAB 1: Spend Intelligence ══ */}
      {activeTab === 'intelligence' && (
        <div className="space-y-6">
          {cards.length === 0 ? (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 py-16 text-center">
              <BarChart3 className="mx-auto h-10 w-10 text-slate-600 mb-3" />
              <p className="text-sm font-medium text-slate-200">Import your cards to see spend intelligence</p>
              <button onClick={openDbPicker} className="mt-3 text-sm font-medium text-indigo-400 hover:text-indigo-300">
                <Database className="inline h-4 w-4 mr-1" /> Browse Card Database
              </button>
            </div>
          ) : (<>
            {/* Summary Tiles */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-5">
                <div className="flex items-center gap-2 mb-1"><Gift className="h-4 w-4 text-emerald-400" /><span className="text-xs font-medium text-emerald-300">Earned</span></div>
                <p className="text-2xl font-bold text-emerald-400">{fmt(data?.total_earned ?? 0)}</p>
              </div>
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
                <div className="flex items-center gap-2 mb-1"><AlertTriangle className="h-4 w-4 text-amber-400" /><span className="text-xs font-medium text-amber-300">Missed</span></div>
                <p className="text-2xl font-bold text-amber-400">{fmt(data?.total_missed ?? 0)}</p>
              </div>
              <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/10 p-5">
                <div className="flex items-center gap-2 mb-1"><TrendingUp className="h-4 w-4 text-indigo-400" /><span className="text-xs font-medium text-indigo-300">Optimization Rate</span></div>
                <p className="text-2xl font-bold text-indigo-400">
                  {(data?.total_earned ?? 0) + (data?.total_missed ?? 0) > 0
                    ? pct(((data?.total_earned ?? 0) / ((data?.total_earned ?? 0) + (data?.total_missed ?? 0))) * 100) : '—'}
                </p>
              </div>
            </div>

            {/* Missed & Earned Rewards Tables */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-amber-500/20 bg-slate-800/80 overflow-hidden">
                <button onClick={() => setExpandedMissed(!expandedMissed)} className="flex w-full items-center justify-between p-4 border-b border-slate-700/30">
                  <span className="text-sm font-medium text-amber-300 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Missed Rewards ({missed.length})</span>
                  {expandedMissed ? <ChevronUp className="h-4 w-4 text-amber-400" /> : <ChevronDown className="h-4 w-4 text-amber-400" />}
                </button>
                {expandedMissed && (
                  missed.length === 0 ? (
                    <p className="py-6 text-center text-xs text-slate-500">No missed rewards — great job!</p>
                  ) : (
                    <div className="max-h-80 overflow-y-auto overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-slate-500 bg-slate-900/50">
                            <th className="px-3 sm:px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleMissedSort('merchant')}>
                              <span className="inline-flex items-center gap-1">Merchant <SortIcon col="merchant" sort={missedSort} /></span>
                            </th>
                            <th className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleMissedSort('card_used')}>
                              <span className="inline-flex items-center gap-1">Card Used <SortIcon col="card_used" sort={missedSort} /></span>
                            </th>
                            <th className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleMissedSort('better_card')}>
                              <span className="inline-flex items-center gap-1">Better Card <SortIcon col="better_card" sort={missedSort} /></span>
                            </th>
                            <th className="px-4 py-2 text-right font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleMissedSort('difference')}>
                              <span className="inline-flex items-center gap-1 justify-end">Missed <SortIcon col="difference" sort={missedSort} /></span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/20">
                          {[...missed].sort((a, b) => { const d = missedSort.dir === 'asc' ? 1 : -1; const col = missedSort.col as keyof MissedReward; const av = a[col], bv = b[col]; return typeof av === 'number' ? d * ((av as number) - (bv as number)) : d * String(av).localeCompare(String(bv)) }).map((m, i) => (
                            <tr key={i} className="text-slate-400 hover:bg-slate-700/20 transition-colors">
                              <td className="px-4 py-2">
                                <p className="text-slate-300 font-medium">{m.merchant}</p>
                                <p className="text-[10px] text-slate-600">{CAT_ICONS[m.category] || ''} {m.category} · {fmt(m.amount)}</p>
                              </td>
                              <td className="px-4 py-2 text-slate-500">{m.card_used}</td>
                              <td className="px-4 py-2 text-amber-300 font-medium">{m.better_card}</td>
                              <td className="px-4 py-2 text-right font-bold text-amber-400 tabular-nums">-{fmt(m.difference)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
              <div className="rounded-xl border border-emerald-500/20 bg-slate-800/80 overflow-hidden">
                <button onClick={() => setExpandedEarned(!expandedEarned)} className="flex w-full items-center justify-between p-4 border-b border-slate-700/30">
                  <span className="text-sm font-medium text-emerald-300 flex items-center gap-2"><Gift className="h-4 w-4" /> Earned Rewards ({earned.length})</span>
                  {expandedEarned ? <ChevronUp className="h-4 w-4 text-emerald-400" /> : <ChevronDown className="h-4 w-4 text-emerald-400" />}
                </button>
                {expandedEarned && (
                  earned.length === 0 ? (
                    <p className="py-6 text-center text-xs text-slate-500">No earned rewards yet</p>
                  ) : (
                    <div className="max-h-80 overflow-y-auto overflow-x-auto">
                      <table className="min-w-full text-xs">
                        <thead>
                          <tr className="text-slate-500 bg-slate-900/50">
                            <th className="px-3 sm:px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleEarnedSort('merchant')}>
                              <span className="inline-flex items-center gap-1">Merchant <SortIcon col="merchant" sort={earnedSort} /></span>
                            </th>
                            <th className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleEarnedSort('card_used')}>
                              <span className="inline-flex items-center gap-1">Card <SortIcon col="card_used" sort={earnedSort} /></span>
                            </th>
                            <th className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleEarnedSort('category')}>
                              <span className="inline-flex items-center gap-1">Category <SortIcon col="category" sort={earnedSort} /></span>
                            </th>
                            <th className="px-4 py-2 text-right font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleEarnedSort('reward_earned')}>
                              <span className="inline-flex items-center gap-1 justify-end">Earned <SortIcon col="reward_earned" sort={earnedSort} /></span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/20">
                          {[...earned].sort((a, b) => { const d = earnedSort.dir === 'asc' ? 1 : -1; const col = earnedSort.col as keyof EarnedReward; const av = a[col], bv = b[col]; return typeof av === 'number' ? d * ((av as number) - (bv as number)) : d * String(av).localeCompare(String(bv)) }).map((e, i) => (
                            <tr key={i} className="text-slate-400 hover:bg-slate-700/20 transition-colors">
                              <td className="px-4 py-2">
                                <p className="text-slate-300 font-medium">{e.merchant}</p>
                                <p className="text-[10px] text-slate-600">{fmt(e.amount)} spent</p>
                              </td>
                              <td className="px-4 py-2 text-slate-500">{e.card_used}</td>
                              <td className="px-4 py-2 text-slate-500">{CAT_ICONS[e.category] || ''} {e.category}</td>
                              <td className="px-4 py-2 text-right font-bold text-emerald-400 tabular-nums">+{fmt(e.reward_earned)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            </div>

            {/* Category Breakdown */}
            {catBreakdown.length > 0 && (
              <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
                <div className="p-4 border-b border-slate-700/30">
                  <h3 className="font-semibold text-white flex items-center gap-2"><BarChart3 className="h-4 w-4 text-indigo-400" /> Rewards by Category</h3>
                  <p className="text-xs text-slate-500 mt-0.5">How your spending performs across categories</p>
                </div>
                <div className="divide-y divide-slate-700/30">
                  {catBreakdown.map(cat => {
                    const efficiency = cat.optimal > 0 ? (cat.earned / cat.optimal) * 100 : 100
                    return (
                      <div key={cat.category} className="p-4 hover:bg-slate-800/50 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{CAT_ICONS[cat.category] || '🏷️'}</span>
                            <span className="text-sm font-medium text-white capitalize">{cat.category}</span>
                            <span className="text-xs text-slate-500">{cat.txn_count} txn{cat.txn_count !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-slate-200">{fmt(cat.spend)}</span>
                            <span className="text-xs text-slate-500 ml-1">spent</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 rounded-full bg-slate-700 overflow-hidden">
                            <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all"
                              style={{ width: `${Math.min(efficiency, 100)}%` }} />
                          </div>
                          <span className={`text-xs font-medium w-12 text-right ${efficiency >= 90 ? 'text-emerald-400' : efficiency >= 50 ? 'text-amber-400' : 'text-rose-400'}`}>
                            {pct(efficiency)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1.5 text-xs">
                          <span className="text-emerald-400">Earned: {fmt(cat.earned)}</span>
                          {cat.missed > 0.01
                            ? <span className="text-amber-400">Missed: {fmt(cat.missed)} — use <span className="font-medium text-amber-300">{cat.best_card}</span> ({pct(cat.best_rate)})</span>
                            : <span className="text-emerald-400/60">Optimal card used</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Per-Card Performance */}
            {cardStats.length > 0 && (
              <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
                <div className="p-4 border-b border-slate-700/30">
                  <h3 className="font-semibold text-white flex items-center gap-2"><CreditCard className="h-4 w-4 text-indigo-400" /> Card Performance</h3>
                </div>
                <div className="divide-y divide-slate-700/30">
                  {cardStats.map(cs => (
                    <div key={cs.card_id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-white">{cs.card_name}</p>
                        <p className="text-xs text-slate-500">{cs.txn_count} transactions · {fmt(cs.spend)} spend</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-emerald-400">{fmt(cs.earned)} earned</p>
                        {cs.missed > 0.01 && <p className="text-xs text-amber-400">{fmt(cs.missed)} missed</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </>)}
        </div>
      )}

      {/* ══ TAB 2: Your Cards ══ */}
      {activeTab === 'cards' && (
        <div>
          {cards.length === 0 ? (
            <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 py-16 text-center">
              <CreditCard className="mx-auto h-10 w-10 text-slate-600 mb-3" />
              <p className="text-sm font-medium text-slate-200">No reward cards configured</p>
              <p className="mt-1 text-sm text-slate-500">Import from our database of 26+ popular cards or add manually</p>
              <div className="mt-4 flex items-center justify-center gap-3">
                <button onClick={openDbPicker} className="flex items-center gap-1.5 text-sm font-medium text-indigo-400 hover:text-indigo-300"><Database className="h-4 w-4" /> Browse Card Database</button>
                <span className="text-slate-600">or</span>
                <button onClick={addManualCard} className="text-sm font-medium text-slate-400 hover:text-slate-300">Add manually →</button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {cards.map((card) => {
                const isEditing = editingCard === card.card_id
                const isFromDb = !!card.db_card_id
                const isExpanded = expandedCardId === card.card_id
                const stat = cardStats.find(s => s.card_id === card.card_id)
                return (
                  <div key={card.card_id} className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
                    {/* Card Header — clickable to expand */}
                    <div className="flex items-center justify-between p-4 border-b border-slate-700/30 cursor-pointer"
                      onClick={() => !isEditing && setExpandedCardId(isExpanded ? null : card.card_id)}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${isFromDb ? 'bg-indigo-500/20 text-indigo-400' : 'bg-slate-700/50 text-slate-400'}`}>
                          <CreditCard className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-semibold text-sm text-slate-200 truncate">{card.card_name || 'Unnamed Card'}</p>
                            {isFromDb && <span className="rounded bg-indigo-500/20 px-1.5 py-0.5 text-[10px] font-medium text-indigo-300">DB</span>}
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>{card.institution}{card.last4 ? ` ···${card.last4}` : ''}</span>
                            {card.card_network && <span>· {card.card_network}</span>}
                            {card.rewards_currency && <span>· {card.rewards_currency}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {stat && <span className="text-xs text-emerald-400 font-medium hidden sm:block">{fmt(stat.earned)} earned</span>}
                        {!isEditing && (
                          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setEditingCard(card.card_id)} className="text-xs text-indigo-400 hover:text-indigo-300">Edit</button>
                            <button onClick={() => deleteCard(card.card_id)} className="text-slate-500 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        )}
                        {!isEditing && (isExpanded ? <ChevronUp className="h-4 w-4 text-slate-500" /> : <ChevronDown className="h-4 w-4 text-slate-500" />)}
                      </div>
                    </div>

                    {/* Editing mode */}
                    {isEditing && (
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-400">Institution</label>
                            <input value={card.institution} onChange={(e) => setData(prev => prev ? { ...prev, cards: prev.cards.map(c => c.card_id === card.card_id ? { ...c, institution: e.target.value } : c) } : prev)}
                              className="w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-white mt-1" />
                          </div>
                          <div>
                            <label className="text-xs text-slate-400">Last 4 digits</label>
                            <input value={card.last4} maxLength={4} onChange={(e) => setData(prev => prev ? { ...prev, cards: prev.cards.map(c => c.card_id === card.card_id ? { ...c, last4: e.target.value } : c) } : prev)}
                              className="w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-sm text-white mt-1" />
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-slate-400 mb-2">Category Rates (cashback %)</p>
                          <div className="grid grid-cols-3 gap-2">
                            {card.rates.map((rate, ri) => (
                              <div key={ri} className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 w-20 truncate capitalize">{rate.category}</span>
                                <input type="number" step="0.1" min="0" max="100" value={rate.cashback_pct}
                                  onChange={(e) => {
                                    const newRates = [...card.rates]
                                    newRates[ri] = { ...newRates[ri], cashback_pct: parseFloat(e.target.value) || 0 }
                                    setData(prev => prev ? { ...prev, cards: prev.cards.map(c => c.card_id === card.card_id ? { ...c, rates: newRates } : c) } : prev)
                                  }}
                                  className="w-16 rounded border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-white" />
                                <span className="text-xs text-slate-600">%</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                          <button onClick={() => setEditingCard(null)} className="px-3 py-1.5 text-sm text-slate-400 hover:text-white">Cancel</button>
                          <button onClick={() => saveCardRates(card)} disabled={saving}
                            className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Expanded card detail */}
                    {isExpanded && !isEditing && (
                      <div className="p-4 space-y-4">
                        {/* Reward rates grid */}
                        <div>
                          <p className="text-xs font-medium text-slate-400 mb-2 flex items-center gap-1"><Info className="h-3 w-3" /> Reward Rates</p>
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                            {card.rates.map((r, i) => (
                              <div key={i} className={`rounded-lg p-2.5 text-center border ${
                                r.cashback_pct > card.default_rate ? 'border-indigo-500/30 bg-indigo-500/10' : 'border-slate-700/30 bg-slate-800/50'
                              }`}>
                                <span className="text-lg">{CAT_ICONS[r.category] || '🏷️'}</span>
                                <p className="text-[10px] text-slate-400 capitalize mt-0.5">{r.category}</p>
                                <p className={`text-sm font-bold mt-0.5 ${r.cashback_pct > card.default_rate ? 'text-indigo-300' : 'text-slate-300'}`}>
                                  {r.cashback_pct}%
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Card metadata */}
                        {isFromDb && (
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {[
                              { label: 'Annual Fee', value: (card.annual_fee ?? 0) > 0 ? fmt(card.annual_fee!) : 'None', color: (card.annual_fee ?? 0) > 0 ? 'text-slate-300' : 'text-emerald-400' },
                              { label: 'Annual Credits', value: (card.annual_credits_value ?? 0) > 0 ? fmt(card.annual_credits_value!) : 'None', color: (card.annual_credits_value ?? 0) > 0 ? 'text-emerald-400' : 'text-slate-500' },
                              { label: 'Foreign Tx Fee', value: (card.foreign_tx_fee_pct ?? 0) === 0 ? 'None' : `${card.foreign_tx_fee_pct}%`, color: (card.foreign_tx_fee_pct ?? 0) === 0 ? 'text-emerald-400' : 'text-amber-400' },
                              { label: 'Point Value', value: `${card.point_value_cents ?? 1}¢`, color: (card.point_value_cents ?? 1) > 1 ? 'text-indigo-400' : 'text-slate-300' },
                            ].map(item => (
                              <div key={item.label} className="rounded-lg border border-slate-700/30 bg-slate-800/50 p-3">
                                <p className="text-[10px] text-slate-500">{item.label}</p>
                                <p className={`text-sm font-semibold ${item.color}`}>{item.value}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {isFromDb && (
                          <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                            {card.has_transfer_partners && <span className="flex items-center gap-1 text-violet-400"><Plane className="h-3 w-3" /> Has transfer partners</span>}
                            {(card.welcome_bonus_value ?? 0) > 0 && <span className="text-amber-400">Welcome bonus: {fmt(card.welcome_bonus_value!)}</span>}
                          </div>
                        )}
                        {stat && (
                          <div className="rounded-lg border border-slate-700/30 bg-slate-800/50 p-3">
                            <p className="text-xs text-slate-500 mb-1">This Period</p>
                            <div className="flex items-center gap-4 text-xs">
                              <span className="text-slate-300">{stat.txn_count} txns · {fmt(stat.spend)} spend</span>
                              <span className="text-emerald-400 font-medium">{fmt(stat.earned)} earned</span>
                              {stat.missed > 0.01 && <span className="text-amber-400">{fmt(stat.missed)} missed</span>}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Collapsed pills */}
                    {!isExpanded && !isEditing && (
                      <div className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {card.rates.filter(r => r.cashback_pct > card.default_rate).map((r, i) => (
                            <span key={i} className="rounded-full bg-indigo-500/10 border border-indigo-500/30 px-2 py-0.5 text-xs text-indigo-300">
                              {CAT_ICONS[r.category] || ''} {r.category}: {r.cashback_pct}%
                            </span>
                          ))}
                          <span className="rounded-full bg-slate-700/50 px-2 py-0.5 text-xs text-slate-400">base: {card.default_rate}%</span>
                          {isFromDb && (card.annual_fee ?? 0) > 0 && (
                            <span className="rounded-full bg-slate-700/50 px-2 py-0.5 text-xs text-slate-500">
                              <DollarSign className="inline h-3 w-3" />{fmt(card.annual_fee!)}/yr
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ TAB 3: SmartPay Advisor ══ */}
      {activeTab === 'advisor' && (
        <div className="space-y-6">
          {/* Input */}
          <div className="rounded-xl border border-indigo-500/30 bg-indigo-500/5 p-5 space-y-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-5 w-5 text-indigo-400" />
              <h3 className="font-semibold text-white">Which card should I use?</h3>
            </div>
            <p className="text-xs text-slate-400">Describe what you're buying and we'll recommend the best card from your wallet.</p>
            <div className="space-y-3">
              <input value={advisorQuery} onChange={e => setAdvisorQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && askAdvisor()}
                placeholder='e.g. "booking a flight on chase travel" or "dinner at a restaurant"'
                className="w-full rounded-lg border border-slate-700 bg-slate-800 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none" />
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-xs text-slate-400">Amount:</label>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-500">$</span>
                    <input type="number" value={advisorAmount} onChange={e => setAdvisorAmount(e.target.value)} placeholder="100"
                      className="w-24 rounded-lg border border-slate-700 bg-slate-800 pl-5 pr-2 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none" />
                  </div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-slate-400 cursor-pointer">
                  <input type="checkbox" checked={advisorIntl} onChange={e => setAdvisorIntl(e.target.checked)}
                    className="rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500 h-3.5 w-3.5" />
                  International
                </label>
                <div className="flex-1" />
                <button onClick={() => askAdvisor()} disabled={advisorLoading || !advisorQuery.trim() || cards.length === 0}
                  className="flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50 transition-colors">
                  {advisorLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Recommend
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {['Booking a flight', 'Dinner at a restaurant', 'Groceries', 'Gas station', 'Hotel on Chase Travel', 'Amazon order', 'Netflix subscription'].map(q => (
                  <button key={q} onClick={() => askAdvisor(q)}
                    className="rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-xs text-slate-400 hover:border-indigo-500/50 hover:text-indigo-300 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {cards.length === 0 && (
            <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-4 text-sm text-amber-300 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Add at least one card to get recommendations.
            </div>
          )}

          {/* Result */}
          {advisorResult && (
            <div className="space-y-4">
              {/* Winner */}
              <div className="rounded-xl border-2 border-indigo-500/50 bg-gradient-to-br from-indigo-500/10 to-slate-800/80 p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <span className="text-xs font-medium text-indigo-400 uppercase tracking-wide">Best Card</span>
                    <h4 className="text-xl font-bold text-white mt-1">{advisorResult.recommended.card_name}</h4>
                    <p className="text-sm text-slate-400 mt-0.5">{advisorResult.recommended.institution}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-indigo-400">{pct(advisorResult.recommended.effective_rate)}</p>
                    <p className="text-xs text-slate-400">effective rate</p>
                    {advisorResult.amount > 0 && <p className="text-lg font-bold text-emerald-400 mt-1">{fmt(advisorResult.recommended.reward_value)}</p>}
                  </div>
                </div>
                {advisorResult.recommended.has_portal_bonus && (
                  <div className="mt-3 rounded-lg bg-amber-500/10 border border-amber-500/30 px-3 py-2 text-xs text-amber-300 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-400" />
                    <span>Portal Bonus: {advisorResult.recommended.portal_bonus_reason}</span>
                  </div>
                )}
                <p className="mt-3 text-sm text-slate-300 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: advisorResult.reason.replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>') }} />
                <div className="mt-3 flex items-center gap-2">
                  <span className="rounded-full bg-slate-700/50 px-2.5 py-0.5 text-xs text-slate-400">
                    {CAT_ICONS[advisorResult.detected_category] || '🏷️'} Detected: {advisorResult.detected_category}
                  </span>
                </div>
              </div>

              {/* Alternatives */}
              {advisorResult.alternatives.length > 0 && (
                <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
                  <div className="p-3 border-b border-slate-700/30"><p className="text-xs font-medium text-slate-400">Other Options</p></div>
                  <div className="divide-y divide-slate-700/30">
                    {advisorResult.alternatives.map((alt, i) => (
                      <div key={i} className="flex items-center justify-between p-3">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-600 font-medium w-5">#{i + 2}</span>
                          <span className="text-sm text-slate-300">{alt.card_name}</span>
                          {alt.has_portal_bonus && <Sparkles className="h-3 w-3 text-amber-400" />}
                        </div>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-slate-400">{pct(alt.effective_rate)}</span>
                          <span className="text-emerald-400 font-medium">{fmt(alt.reward_value)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Tips */}
              {advisorResult.tips.length > 0 && (
                <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 p-4 space-y-2">
                  <p className="text-xs font-medium text-slate-400 mb-1">Pro Tips</p>
                  {advisorResult.tips.map((tip, i) => <p key={i} className="text-sm text-slate-300">{tip}</p>)}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ Database Card Picker Modal ══ */}
      {showDbPicker && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm pt-12 px-4" onClick={() => setShowDbPicker(false)}>
          <div className="w-full max-w-2xl rounded-2xl border border-slate-700/50 bg-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-700/50 p-5">
              <div>
                <h3 className="text-lg font-bold text-white">Card Rewards Database</h3>
                <p className="text-xs text-slate-500 mt-0.5">26 popular credit cards with pre-filled reward rates</p>
              </div>
              <button onClick={() => setShowDbPicker(false)} className="text-slate-500 hover:text-white"><X className="h-5 w-5" /></button>
            </div>
            <div className="border-b border-slate-700/30 p-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                <input ref={searchRef} value={dbSearch} onChange={(e) => setDbSearch(e.target.value)}
                  placeholder="Search cards by name or issuer..."
                  className="w-full rounded-lg border border-slate-700 bg-slate-800 pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none" />
              </div>
              <div className="flex flex-wrap gap-2">
                {['all', 'travel', 'cash back', 'business', 'student', 'secured'].map((f) => (
                  <button key={f} onClick={() => setDbFilter(f)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      dbFilter === f ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white'
                    }`}>
                    {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            <div className="max-h-[60vh] overflow-y-auto p-2">
              {dbLoading ? (
                <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
              ) : filteredDbCards.length === 0 ? (
                <div className="py-12 text-center text-sm text-slate-500">No cards match your search</div>
              ) : (
                <div className="space-y-2">
                  {filteredDbCards.map((card) => {
                    const alreadyAdded = existingDbCardIds.has(card.card_id)
                    const isImporting = importing === card.card_id
                    return (
                      <div key={card.card_id} className={`rounded-xl border p-4 transition-colors ${
                        alreadyAdded ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-slate-700/50 bg-slate-800/60 hover:border-indigo-500/40 hover:bg-slate-800'
                      }`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h4 className="text-sm font-semibold text-white truncate">{card.card_name}</h4>
                              <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium ${issuerColor(card.issuer)}`}>{card.issuer}</span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              {card.top_categories.map((tc, i) => (
                                <span key={i} className="rounded-full bg-indigo-500/10 border border-indigo-500/20 px-2 py-0.5 text-[11px] text-indigo-300">
                                  {tc.category}: {tc.earn_rate}{tc.unit.includes('points') ? 'x' : tc.unit.includes('%') ? '%' : 'x'}
                                </span>
                              ))}
                              <span className="rounded-full bg-slate-700/50 px-2 py-0.5 text-[11px] text-slate-500">
                                base: {card.base_earn_rate}{card.base_earn_rate_unit.includes('points') ? 'x' : card.base_earn_rate_unit.includes('%') ? '%' : 'x'}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-slate-500">
                              {card.annual_fee_usd > 0 ? <span>{fmt(card.annual_fee_usd)}/yr</span> : <span className="text-emerald-400">No annual fee</span>}
                              {card.annual_credits_value > 0 && <span className="text-emerald-400">{fmt(card.annual_credits_value)} credits</span>}
                              {card.foreign_transaction_fee_pct === 0 && <span className="text-emerald-400">No FTF</span>}
                              {card.has_transfer_partners && <span className="text-violet-400">Transfer partners</span>}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            {alreadyAdded ? (
                              <span className="rounded-lg bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-400">Added</span>
                            ) : (
                              <button onClick={() => importCard(card.card_id)} disabled={isImporting}
                                className="flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-500 disabled:opacity-50 transition-colors">
                                {isImporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
                                Import
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
